import endpoints from '@app/src/api/endpoints'
import { FetchKey } from '@app/src/api/fetchHooks'
import { useDeleteResource } from '@app/src/api/updateHooks'
import { HasWriteAccess } from '@app/src/components/HasWriteAccess'
import DateCell from '@app/src/components/Table/Cells/DateCell'
import IconCell from '@app/src/components/Table/Cells/IconCell'
import UserChipCell from '@app/src/components/Table/Cells/UserChipCell'
import { useAmplitude } from '@app/src/context/AmplitudeContext'
import { useSnackbar } from '@app/src/context/SnackbarContext'
import useErrorNotification from '@app/src/hooks/errorNotification'
import sentryService from '@app/src/services/SentryService'
import type { ProviderComment } from '@app/src/types/resourceExplorer'
import { AmplitudeTrackingEvents } from '@app/src/wf-constants'
import { DeleteOutlined, InsertCommentOutlined } from '@mui/icons-material'
import { IconButton, TableRow } from '@mui/material'
import { useConfirm } from 'material-ui-confirm'
import React from 'react'
import { useIntl } from 'react-intl'
import { useQueryClient } from '@tanstack/react-query'

interface ProviderCommentRowProps {
  row: ProviderComment
}

export const ProviderCommentRow: React.FC<ProviderCommentRowProps> = ({ row: comment }) => {
  const { formatMessage } = useIntl()
  const { trackEvent } = useAmplitude()
  const { showErrorNotification } = useErrorNotification()
  const { showSnackbar } = useSnackbar()
  const { mutate: mutateDeleteComment, isPending: isLoading } = useDeleteResource()
  const queryClient = useQueryClient()
  const confirm = useConfirm()

  const confirmDeleteComment = () => {
    confirm({
      content: formatMessage({ id: 'resourceExplorer.activity.deleteCommentContent' }),
      confirmationText: formatMessage({ id: 'general.delete' }),
      cancellationButtonProps: { variant: 'text' },
      confirmationButtonProps: { variant: 'text' },
      buttonOrder: ['confirm', 'cancel'],
    })
      .then(() => {
        mutateDeleteComment(
          {
            url: endpoints.providerCommentById(comment.id),
          },
          {
            onSuccess: () => {
              showSnackbar({
                message: formatMessage({ id: 'notifications.successfulResourceDelete' }),
                severity: 'success',
              })
              trackEvent({
                name: AmplitudeTrackingEvents.Accessor.ProviderOrganization.AdditionalInformation,
                eventProps: {
                  information_type: 'comment',
                  added_or_deleted: 'deleted',
                },
              })
              queryClient.invalidateQueries({ queryKey: [FetchKey.CommentCollection] })
            },
            onError: error => {
              showErrorNotification({ requestError: error })
            },
          },
        )
      })
      .catch(error => {
        if (error && typeof error === 'object' && error.message && !error.message.includes('cancel')) {
          sentryService.captureError(error as Error, {
            tags: {
              errorType: 'dialog-system',
              operation: 'delete-confirmation',
              component: 'comment-row',
            },
            extra: {
              commentId: comment.id,
              providerId: comment.objectInstanceId,
            },
          })
        }
      })
  }

  return (
    <TableRow>
      <IconCell icon={<InsertCommentOutlined color="action" />} title={comment.text} maxRows={4} />
      <DateCell value={comment.createdAt} />
      <UserChipCell user={comment.creatorUser} drawer />
      <HasWriteAccess>
        <IconCell
          icon={
            <IconButton size="small" onClick={confirmDeleteComment} disabled={isLoading}>
              <DeleteOutlined color="action" />
            </IconButton>
          }
        />
      </HasWriteAccess>
    </TableRow>
  )
}

export default ProviderCommentRow
